import React, { useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import UserContext from 'context/userContext';
import { EditableSession } from 'types/event.types';
import { Icon } from '@epoch-ui/components/Icon';
import { Separator } from '@epoch-ui/components/Separator';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import {
  SegmentedProgressChartContainer,
  SegmentedProgressChart,
  SegmentedProgressChartConfig,
  SegmentedProgressChartData
} from '@epoch-components/Charts';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { eventGuestsApi } from '@event-editor/api/event-guests';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

const GuestsSummary: React.FC<{ session?: EditableSession }> = ({ session }) => {
  const user = useContext(UserContext);
  const event = useEditableEvent();

  const eventOrSession = session ?? event;

  const { data: guestStats, isLoading } = useQuery({
    queryKey: eventEditorQueryKeys.eventGuestStats(eventOrSession.id!),
    queryFn: () => eventGuestsApi.getGuestStats(eventOrSession.id!),
    enabled: event.isPublished && (event.totalGuests ?? 0) > 0
  });

  const hasAttendedGuests = (guestStats?.attended ?? 0) > 0;

  const config = useMemo(() => getChartConfig(guestStats ?? {}), [guestStats]);

  const headerInfo = useMemo<{ label: string; value: number | string }>(() => {
    if (event.isUsingEstimatedAttendees) {
      return { label: 'attended', value: `~${event.estimatedAttendees ?? 0}` };
    }

    if (hasAttendedGuests) {
      return { label: 'attended', value: guestStats?.attended ?? 0 };
    }

    return { label: 'going', value: guestStats?.confirmed ?? 0 };
  }, [event, guestStats, hasAttendedGuests]);

  return (
    <div>
      {isLoading ? (
        // Loading skeleton
        <div className="tw-flex tw-flex-col tw-gap-3">
          {/* Chart header */}
          <div className="tw-flex tw-items-end tw-gap-2">
            <Skeleton className="tw-h-9 tw-w-5" />
            <Skeleton className="tw-h-6 tw-w-12" />
          </div>
          {/* Progress chart */}
          <Skeleton className="tw-h-2 tw-w-full" />
          {/* Legend */}
          <div className="tw-flex tw-gap-1">
            <Skeleton className="tw-h-4 tw-w-4" />
            <Skeleton className="tw-h-4 tw-w-16" />
          </div>
        </div>
      ) : (
        <div>
          {/* Chart header */}
          <h3 className="tw-mb-3 tw-text-2xl tw-text-foreground">
            <span className="tw-font-semibold">{headerInfo.value} </span>
            <span className="tw-text-sm tw-text-muted-foreground">{headerInfo.label}</span>
          </h3>

          {/* Progress chart */}
          <SegmentedProgressChartContainer config={config}>
            <SegmentedProgressChart data={{ ...guestStats, attended: 0 }} />
          </SegmentedProgressChartContainer>

          {/* Chart legend */}
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-4">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
              {hasAttendedGuests && !event.isUsingEstimatedAttendees ? (
                <>
                  <div className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-foreground">
                    <Icon
                      name="circle-check"
                      fill="#059450"
                      className="tw-text-background"
                      size={16}
                      strokeWidth={2}
                    />
                    <span>{guestStats?.attended ?? 0} attended</span>
                  </div>
                  <Separator orientation="vertical" className="tw-h-5" />
                </>
              ) : null}

              {Object.entries(config).map(([key, value]) => {
                if (!value.label || guestStats?.[key as keyof typeof guestStats] === 0) return null;

                return (
                  <div key={key} className="tw-flex tw-items-center tw-gap-1">
                    <div
                      className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-[2px]"
                      style={{ backgroundColor: value.color }}
                    />
                    <span className="tw-text-sm">{value.label}</span>
                  </div>
                );
              })}
            </div>
            <Separator orientation="vertical" className="tw-h-5" />
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-muted-foreground">
              <Icon name="user" size={16} />
              <span>{event.totalGuests} total guests</span>
            </div>
          </div>

          {event.hasEnded &&
            guestStats?.attended === 0 &&
            !event.isUsingEstimatedAttendees &&
            user?.company?.confirmedGuestsForAttendance && (
              <div className="tw-mt-3 tw-text-xs tw-font-normal tw-leading-4 tw-text-content-tertiary">
                <Icon name="info" size={16} className="tw-mr-1" />
                Registered guests count as attendees when no guests are marked as attended.
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export { GuestsSummary };

const getChartConfig = (guestStats: SegmentedProgressChartData): SegmentedProgressChartConfig => ({
  confirmed: {
    color: 'hsl(var(--positive-foreground))',
    label: <span className="tw-text-positive-foreground">{guestStats.confirmed} going</span>
  },
  incomplete: {
    color: 'hsl(var(--warning-foreground))',
    label: <span className="tw-text-warning-foreground">{guestStats.incomplete} incomplete</span>
  },
  waitlisted: {
    color: 'hsl(var(--highlight-foreground))',
    label: <span className="tw-text-highlight-foreground">{guestStats.waitlisted} waitlisted</span>
  },
  interested: {
    color: 'hsl(var(--brand-primary))',
    label: <span className="tw-text-brand-primary">{guestStats.interested} maybe</span>
  },
  cancelled: {
    color: 'hsl(var(--negative-foreground))',
    label: <span className="tw-text-negative-foreground">{guestStats.cancelled} not going</span>
  },
  actionRequired: {
    color: 'hsl(var(--border))',
    label: <span className="tw-text-muted-foreground">{guestStats.actionRequired} pending</span>
  },
  invited: {
    color: 'hsl(var(--border))',
    label: <span className="tw-text-muted-foreground">{guestStats.invited} invited</span>
  }
});
